import React, { useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Truck } from "lucide-react";
import api, { apiError } from "@/lib/api";

export const BulkTrackingModal = ({ batch, onClose, onSaved }) => {
  const { t: tr } = useTranslation();
  const [values, setValues] = useState(() =>
    Object.fromEntries((batch.orders || []).map((o) => [o.order_id, o.tracking_number || ""]))
  );
  const [saving, setSaving] = useState(false);

  const setVal = (id, v) => setValues((s) => ({ ...s, [id]: v }));

  const saveAll = async () => {
    const items = (batch.orders || [])
      .map((o) => ({ order_id: o.order_id, tracking_number: (values[o.order_id] || "").trim() }))
      .filter((it) => it.tracking_number);
    if (!items.length) { toast.error(tr("orders.bulkTrackingEmpty")); return; }
    setSaving(true);
    try {
      const { data } = await api.put("/orders/tracking-bulk", { items });
      toast.success(tr("orders.bulkTrackingSaved", { count: data.saved }));
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center" onClick={onClose} data-testid="bulk-tracking-modal">
      <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }} onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-md rounded-t-3xl sm:rounded-3xl p-6 max-h-[85vh] overflow-y-auto">
        <div className="flex items-center gap-2 mb-1"><Truck className="h-6 w-6 text-[#0077B6]" /><h3 className="font-display text-xl font-extrabold">{tr("orders.bulkTrackingTitle")}</h3></div>
        <p className="text-sm text-[#5C574F] mb-4">{batch.carrier ? `${tr("orders.carrierLabel")} : ${batch.carrier}. ` : ""}{tr("orders.bulkTrackingSub")}</p>
        <div className="space-y-3 mb-4">
          {(batch.orders || []).map((o) => (
            <div key={o.order_id} data-testid={`bulk-tracking-row-${o.order_id}`}>
              <div className="flex items-baseline justify-between mb-1">
                <span className="text-sm font-semibold text-[#1A1A1A]">#{o.order_id.slice(-6)}</span>
                <span className="text-xs text-[#9c968a] truncate ml-2 max-w-[60%]">{o.customer_name}</span>
              </div>
              <input
                data-testid={`bulk-tracking-input-${o.order_id}`}
                value={values[o.order_id] || ""}
                onChange={(e) => setVal(o.order_id, e.target.value)}
                placeholder={tr("orders.trackingPlaceholder")}
                className="w-full h-10 rounded-xl border border-[#E5E0D8] px-3 text-sm outline-none focus:border-[#0077B6]"
              />
            </div>
          ))}
        </div>
        <button
          data-testid="bulk-tracking-save-btn"
          onClick={saveAll}
          disabled={saving}
          className="w-full h-12 rounded-2xl bg-[#1B4332] text-white font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-60"
        >
          {saving ? tr("common.sending") : tr("orders.bulkTrackingSaveAll")}
        </button>
        <button onClick={onClose} className="w-full h-11 mt-2 rounded-2xl text-[#5C574F] font-semibold" data-testid="bulk-tracking-close-btn">{tr("common.close")}</button>
      </motion.div>
    </div>
  );
};
